/**
 * Componente BillingPage
 * 
 * DESCRIÇÃO:
 * Página principal de cobrança e gerenciamento de assinaturas.
 * Exibe o status atual da assinatura, opções de planos disponíveis
 * e permite acesso ao portal do cliente para gerenciamento.
 * Agora com Price IDs dinâmicos baseados no ambiente.
 * 
 * FUNCIONALIDADES:
 * - Exibição do status atual da assinatura
 * - Cards com opções de planos (Basic, Premium)
 * - Price IDs dinâmicos (teste/produção)
 * - Botões de ação para checkout e portal do cliente
 * - Indicador visual do plano atual e ambiente
 * - Estados de loading para operações
 * 
 * INTEGRAÇÃO:
 * - Usa hook useSubscription para gerenciar estado
 * - Conecta com Edge Functions do Stripe
 * - Price IDs sincronizados com as Edge Functions
 * - Atualiza dados em tempo real
 *
 * VISUAL (24/09/2026): Gens Design System via escopo .gens-escopo (src/styles/gens-escopo.css).
 * O plano mais escolhido usa o painel profundo; os demais, cards claros com canhoto de limites.
 */

import React, { useEffect } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { CabecalhoConfiguracoes } from '@/components/settings/layout/CabecalhoConfiguracoes';
import { Button } from '@/components/ui/button';
import { Check, CreditCard, Settings, RefreshCw, AlertTriangle, Sparkles } from 'lucide-react';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { cn } from '@/lib/utils';
import '@/styles/gens-escopo.css';
import { toast } from 'sonner';
import { getCurrentEnvironment, getPriceId, isTestEnvironment } from '@/utils/stripeEnvironment';

const getPlansForEnvironment = () => {
  const currentEnv = getCurrentEnvironment();

  return [
    {
      id: 'basic',
      name: 'Básico',
      description: 'Ideal para clínicas pequenas',
      price: 'R$ 67',
      period: '/mês',
      originalPrice: 'R$ 97',
      features: [
        'IA integrada para conversas',
        'Até 500 leads',
        '2 relatórios mensais de IA',
        'Integração WhatsApp',
        'Pipeline de vendas',
        'Suporte por email'
      ],
      isPopular: false,
      popular: false,
      priceId: getPriceId('basic', currentEnv),
      limits: {
        leads: 500,
        users: 1,
        reports: 2
      }
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Para clínicas em crescimento',
      price: 'R$ 147',
      period: '/mês',
      originalPrice: 'R$ 197',
      features: [
        'Tudo do Básico',
        'Até 1.000 leads',
        'Até 3 usuários',
        '4 relatórios mensais de IA',
        'Integração Instagram',
        'Prontuário médico',
        'Mensagens agendadas',
        'Importação/exportação',
        'Campanhas de follow-up',
        'Suporte prioritário'
      ],
      isPopular: true,
      popular: true,
      priceId: getPriceId('pro', currentEnv),
      limits: {
        leads: 1000,
        users: 3,
        reports: 4
      }
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Para clínicas de alto volume',
      price: 'R$ 247',
      period: '/mês',
      originalPrice: 'R$ 347',
      features: [
        'Tudo do Pro',
        'Leads ilimitados',
        'Usuários ilimitados',
        'Relatórios ilimitados',
        'Analytics avançado',
        'Suporte premium 24/7',
        'Treinamento personalizado'
      ],
      isPopular: false,
      popular: false,
      priceId: getPriceId('premium', currentEnv),
      limits: {
        leads: -1,
        users: -1,
        reports: -1
      }
    }
  ];
};

const BillingPage = () => {
  // Hook para gerenciar todas as operações de assinatura
  const {
    subscriptionData,
    isLoading,
    error,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    isCreatingCheckout,
    isOpeningPortal
  } = useSubscription();

  // Hook de permissões para obter plano real (incluindo PMK e manuais)
  const { planName, isPMKPlan, isLoading: planLoading } = usePlanPermissions();



  // Obter planos com Price IDs dinâmicos
  const PLANS = getPlansForEnvironment();
  const currentEnvironment = getCurrentEnvironment();
  const isTestMode = isTestEnvironment();

  // Exibir mensagens de sucesso/erro vindas da URL (após checkout)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast.success('Assinatura ativada com sucesso!');
      // Verificar status após sucesso
      setTimeout(() => checkSubscription(), 2000);
    } else if (urlParams.get('canceled') === 'true') {
      toast.info('Checkout cancelado.');
    }
  }, []);

  // Função para selecionar um plano e iniciar checkout
  const handleSelectPlan = async (plan: typeof PLANS[0]) => {
    console.log(`[BillingPage] Iniciando checkout para plano ${plan.name} com Price ID: ${plan.priceId} (ambiente: ${currentEnvironment})`);
    await createCheckout(plan.priceId);
  };

  // Função para abrir o portal do cliente
  const handleManageSubscription = async () => {
    await openCustomerPortal();
  };

  // Função para obter o plano atual do usuário
  const getCurrentPlan = () => {
    const plans = getPlansForEnvironment();

    // 1. Prioridade: Plano PMK
    if (isPMKPlan || planName === 'pmk') {
      return {
        id: 'pmk',
        name: 'Plano PMK (Parceiro)',
        price: 'Personalizado',
        period: '',
        features: ['Acesso total', 'Consultoria exclusiva', 'Suporte VIP'],
        limits: { leads: -1, users: -1, reports: -1 }
      };
    }

    // 2. Plano vindo do usePlanPermissions (que olha o banco corretamente)
    if (planName && planName !== 'free') {
      const foundPlan = plans.find(plan => plan.id === planName);
      if (foundPlan) return foundPlan;
    }

    // 3. Fallback para dados do Stripe (legacy)
    if (subscriptionData) {
      const stripePlan = plans.find(plan => plan.id === subscriptionData.subscription_tier);
      if (stripePlan) return stripePlan;
    }

    // 4. Default: Free
    return {
      id: 'free',
      name: 'Pagamento pendente',
      price: '—',
      period: '',
      features: ['Escolha um plano para liberar o CRM'],
      limits: { leads: 0, users: 0, reports: 0 }
    };
  };

  // Função para formatar datas
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Estado de loading inicial: loader padrão do app, com texto para leitor de tela
  if (isLoading && !subscriptionData && !planName) {
    return (
      <div className="gens-escopo flex min-h-[400px] flex-col items-center justify-center gap-3 text-center">
        <ThinkingOrbLoader state="solving" size={64} label="Carregando informações de cobrança" />
        <p className="text-sm text-muted-foreground">Carregando informações de cobrança...</p>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();
  const pagamentoPendente = currentPlan?.id === 'free';

  // Limites reais do plano viram o "canhoto" do card (sem inventar benefícios).
  const descreverLimite = (valor: number, singular: string, plural: string) =>
    valor === -1 ? `${plural} ilimitados` : `${valor.toLocaleString('pt-BR')} ${valor === 1 ? singular : plural}`;

  return (
    // O escopo .gens-escopo aplica a paleta do design system só nesta página.
    <div className="gens-escopo space-y-5">
      <CabecalhoConfiguracoes
        titulo="Planos e cobrança"
        descricao={
          pagamentoPendente
            ? 'Escolha um plano e conclua o pagamento para liberar o CRM.'
            : 'Gerencie sua assinatura e escolha o plano ideal para sua clínica.'
        }
        icone={CreditCard}
        aviso={
          isTestMode ? (
            <span className="lg-selo lg-selo--aviso">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              Modo de teste: nenhuma cobrança real
            </span>
          ) : null
        }
      />

      {/* Aviso de pagamento pendente: tom de aviso com ícone e texto (nunca só cor) */}
      {pagamentoPendente && (
        <div role="status" className="flex items-start gap-3 rounded-[20px] p-4" style={{ background: 'var(--lg-aviso-fundo)' }}>
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: 'var(--lg-aviso-texto)' }} aria-hidden />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--lg-aviso-texto)' }}>Assinatura necessária</p>
            <p className="mt-1 text-sm text-foreground">
              Sua conta foi criada, mas o acesso às funcionalidades será liberado somente após a confirmação do primeiro pagamento.
            </p>
          </div>
        </div>
      )}

      {/* Plano atual */}
      <section aria-labelledby="titulo-plano-atual" className="lg-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-secondary text-accent-foreground">
            <CreditCard className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="lg-rotulo">Seu plano</p>
            <h2 id="titulo-plano-atual" className="lg-display mt-0.5 text-xl leading-tight text-foreground">
              {currentPlan?.name || 'Pagamento pendente'}
            </h2>
            {subscriptionData?.subscribed && subscriptionData?.subscription_end && (
              <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                Renova em {formatDate(subscriptionData.subscription_end)}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('lg-selo', pagamentoPendente ? 'lg-selo--aviso' : 'lg-selo--sucesso')}>
            {pagamentoPendente ? <AlertTriangle className="h-3 w-3" aria-hidden /> : <Check className="h-3 w-3" aria-hidden />}
            {pagamentoPendente ? 'Aguardando pagamento' : 'Ativo'}
          </span>
          {subscriptionData?.subscribed && (
            <Button
              onClick={handleManageSubscription}
              disabled={isOpeningPortal}
              variant="outline"
              className="h-10 rounded-full px-4"
            >
              <Settings className="h-4 w-4" />
              {isOpeningPortal ? 'Abrindo portal...' : 'Gerenciar assinatura'}
            </Button>
          )}
        </div>
      </section>

      {/* Planos: o mais escolhido usa o painel profundo (um destaque por tela) */}
      <section aria-labelledby="titulo-planos" className="space-y-3">
        <h2 id="titulo-planos" className="lg-display text-lg text-foreground">Escolha seu plano</h2>
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-stretch">
          {PLANS.map(plan => {
            const isCurrentPlan = currentPlan?.id === plan.id;
            const destaque = plan.popular;

            return (
              <li
                key={plan.id}
                className={cn(
                  'relative flex flex-col overflow-hidden rounded-[24px] border p-5',
                  destaque
                    ? 'border-transparent text-white shadow-[0_2px_4px_rgba(19,41,27,0.05),0_14px_32px_-8px_rgba(19,41,27,0.1),0_36px_72px_-24px_rgba(19,41,27,0.18)]'
                    : 'lg-card',
                  isCurrentPlan && 'ring-[3px] ring-[rgba(186,218,85,0.55)]'
                )}
                style={destaque ? { background: 'linear-gradient(140deg, #0f2e1d 0%, #1c5132 60%, #2c6e49 100%)' } : undefined}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className={cn('lg-display text-lg', destaque ? 'text-white' : 'text-foreground')}>{plan.name}</h3>
                    <p className={cn('mt-0.5 text-sm', destaque ? 'text-[#d3e6c4]' : 'text-muted-foreground')}>{plan.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {destaque && (
                      <span className="lg-selo" style={{ background: '#d8ff3c', color: '#13291b' }}>
                        <Sparkles className="h-3 w-3" aria-hidden />
                        Mais escolhido
                      </span>
                    )}
                    {isCurrentPlan && (
                      <span className="lg-selo lg-selo--sucesso">
                        <Check className="h-3 w-3" aria-hidden />
                        Seu plano
                      </span>
                    )}
                  </div>
                </div>

                <p className="mt-5 flex items-baseline gap-1">
                  <span className={cn('lg-display text-[32px] font-bold tabular-nums tracking-[-0.04em]', destaque ? 'text-white' : 'text-foreground')}>
                    {plan.price}
                  </span>
                  <span className={cn('text-sm', destaque ? 'text-[#d3e6c4]' : 'text-muted-foreground')}>{plan.period}</span>
                </p>

                {/* Lista completa: comparar planos exige ver todos os recursos */}
                <ul className="mt-5 flex-1 space-y-2" aria-label={`Recursos do plano ${plan.name}`}>
                  {plan.features.map((feature) => (
                    <li key={feature} className={cn('flex items-start gap-2 text-sm', destaque ? 'text-white' : 'text-foreground')}>
                      <Check className={cn('mt-0.5 h-4 w-4 shrink-0', destaque ? 'text-[#d8ff3c]' : 'text-accent-foreground')} aria-hidden />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Canhoto: limites reais do plano, separados por linha tracejada */}
                <p
                  className={cn(
                    'mt-5 flex flex-wrap justify-between gap-x-3 gap-y-1 border-t border-dashed pt-3 text-[11px] font-semibold uppercase tracking-[0.06em]',
                    destaque ? 'border-white/30 text-[#d3e6c4]' : 'border-input text-muted-foreground'
                  )}
                >
                  <span>{descreverLimite(plan.limits.leads, 'lead', 'leads')}</span>
                  <span>{descreverLimite(plan.limits.users, 'usuário', 'usuários')}</span>
                </p>

                <Button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isCreatingCheckout || isCurrentPlan}
                  aria-busy={isCreatingCheckout}
                  variant={isCurrentPlan ? 'secondary' : 'default'}
                  className={cn(
                    'mt-4 h-11 w-full rounded-full font-semibold',
                    !isCurrentPlan && 'hover:bg-[hsl(var(--brand-soft))]'
                  )}
                >
                  {isCurrentPlan ? 'Plano atual' : isCreatingCheckout ? 'Abrindo pagamento...' : `Escolher ${plan.name}`}
                </Button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Atualizar status: útil logo após pagar, enquanto o Stripe confirma */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={checkSubscription}
          disabled={isLoading}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
          {isLoading ? 'Atualizando...' : 'Atualizar status'}
        </Button>
      </div>

      {/* Informações de depuração só no ambiente de teste */}
      {isTestMode && (
        <div className="lg-painel-suave space-y-0.5 p-3 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">Depuração (modo de teste)</p>
          <p>
            <strong>Ambiente:</strong> {currentEnvironment} · <strong>Status:</strong> {subscriptionData?.subscribed ? 'Ativa' : 'Inativa'} · <strong>Plano:</strong> {currentPlan?.id || 'Nenhum'}
          </p>
        </div>
      )}

      {/* Erros: mensagem vinda do hook, em destaque de perigo */}
      {error && (
        <div role="alert" className="lg-fundo-perigo flex items-start gap-3 rounded-[20px] p-4">
          <AlertTriangle className="lg-texto-perigo mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <p className="text-sm text-foreground">{error}</p>
        </div>
      )}
    </div>
  );
};
export default BillingPage;
